import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import styles from "./Auth.module.css";
import {
  useRegisterUserMutation,
  useVerifyOtpMutation,
  useResendOtpMutation,
} from "../../store/api/authApi";
import { useDispatch } from "react-redux";
import { login as loginAction } from "../../store/authSlice";
import Logo from "../../components/Logo";

const Register = () => {
  const [registerUserApi] = useRegisterUserMutation();
  const [verifyOtpApi] = useVerifyOtpMutation();
  const [resendOtpApi] = useResendOtpMutation();
  const dispatch = useDispatch();

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    otp: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState(1);
  const navigate = useNavigate();

  const togglePassword = () => setShowPassword((prev) => !prev);

  const validateForm = () => {
    if (!formData.email) {
      setError("Email is required");
      return false;
    }
    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      setError("Please enter a valid email address");
      return false;
    }
    if (!formData.password) {
      setError("Password is required");
      return false;
    }
    const strongPasswordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;
    if (!strongPasswordRegex.test(formData.password)) {
      setError(
        "Password must be at least 8 characters and include uppercase, lowercase, number, and special character.",
      );
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return false;
    }
    return true;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setError("");
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setError("");
    setLoading(true);
    try {
      await registerUserApi({
        email: formData.email,
        password: formData.password,
      }).unwrap();
      setStep(2);
      setError("");
    } catch (err) {
      setError(
        err.data?.message ||
          err.message ||
          "Registration failed. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const response = await verifyOtpApi({
        email: formData.email,
        otp: formData.otp,
        password: formData.password,
      }).unwrap();
      dispatch(
        loginAction({
          token: response.token,
          userId: response.userId,
          profileComplete:
            response.profileCompleted ?? response.profileComplete ?? false,
        }),
      );
      navigate("/user-details");
    } catch (err) {
      setError(
        err.data?.message || err.message || "Invalid OTP. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBackToRegister = () => {
    setStep(1);
    setError("");
    setFormData((prev) => ({ ...prev, otp: "" }));
  };

  const handleResendOTP = async () => {
    setError("");
    setLoading(true);
    try {
      await resendOtpApi({ email: formData.email }).unwrap();
      setError("OTP resent successfully!");
    } catch (err) {
      setError(
        err.data?.message ||
          err.message ||
          "Failed to resend OTP. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        {/* ── Left brand panel ── */}
        <div className={styles.brandPanel}>
          <div className={styles.brandInner}>
            <Logo height={140} scale={3} />

            <p className={styles.brandTagline}>
              {step === 1
                ? "Join a circle of driven minds. Your next big idea starts with the right people around you."
                : "One last step — verify your email to activate your Nessefy account."}
            </p>
            <div className={styles.brandStep}>
              <span
                className={`${styles.stepDot} ${step === 1 ? styles.stepActive : styles.stepDone}`}
              />
              <span className={styles.stepLine} />
              <span
                className={`${styles.stepDot} ${step === 2 ? styles.stepActive : ""}`}
              />
            </div>
            <p className={styles.stepLabel}>
              {step === 1
                ? "Step 1 of 2 — Account Details"
                : "Step 2 of 2 — Email Verification"}
            </p>
          </div>
        </div>

        {/* ── Right form panel ── */}
        <div className={styles.formPanel}>
          <div className={styles.formInner}>
            <div className={styles.formHeader}>
              <h2 className={styles.heading}>
                {step === 1 ? "Create Account" : "Verify Your Email"}
              </h2>
              <p className={styles.subheading}>
                {step === 1
                  ? "Fill in your details to get started"
                  : `We've sent a code to ${formData.email}`}
              </p>
            </div>

            {error && (
              <p
                className={
                  error === "OTP resent successfully!"
                    ? styles.successMsg
                    : styles.error
                }
              >
                {error}
              </p>
            )}

            <form
              onSubmit={step === 1 ? handleRegister : handleVerifyOTP}
              noValidate
            >
              <div className={styles.formGroup}>
                {step === 1 ? (
                  <>
                    <div className={styles.fieldGroup}>
                      <label className={styles.label} htmlFor="email">
                        Email Address
                      </label>
                      <input
                        id="email"
                        type="email"
                        name="email"
                        placeholder="Enter your email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        disabled={loading}
                        className={styles.input}
                      />
                    </div>

                    <div className={styles.fieldGroup}>
                      <label className={styles.label} htmlFor="password">
                        Password
                      </label>
                      <div className={styles.passwordWrapper}>
                        <input
                          id="password"
                          type={showPassword ? "text" : "password"}
                          name="password"
                          placeholder="Create a password"
                          value={formData.password}
                          onChange={handleChange}
                          required
                          disabled={loading}
                          className={styles.input}
                        />
                        <span
                          className={styles.eyeIcon}
                          onClick={togglePassword}
                        >
                          {showPassword ? <FaEyeSlash /> : <FaEye />}
                        </span>
                      </div>
                    </div>

                    <div className={styles.fieldGroup}>
                      <label className={styles.label} htmlFor="confirmPassword">
                        Confirm Password
                      </label>
                      <input
                        id="confirmPassword"
                        type="password"
                        name="confirmPassword"
                        placeholder="Repeat your password"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        required
                        disabled={loading}
                        className={styles.input}
                      />
                    </div>

                    <button
                      type="submit"
                      className={styles.button}
                      disabled={loading}
                    >
                      {loading ? "Creating Account…" : "Create Account"}
                    </button>
                  </>
                ) : (
                  <>
                    <div className={styles.otpGroup}>
                      <label className={styles.label} htmlFor="otp">
                        Verification Code
                      </label>
                      <input
                        id="otp"
                        type="text"
                        name="otp"
                        placeholder="Enter 6-digit OTP"
                        value={formData.otp}
                        onChange={handleChange}
                        required
                        disabled={loading}
                        maxLength="6"
                        className={`${styles.input} ${styles.otpInput}`}
                      />
                    </div>

                    <button
                      type="submit"
                      className={styles.button}
                      disabled={loading}
                    >
                      {loading ? "Verifying…" : "Verify & Continue"}
                    </button>

                    <div className={styles.otpActions}>
                      <button
                        type="button"
                        onClick={handleResendOTP}
                        className={styles.linkButton}
                        disabled={loading}
                      >
                        Resend Code
                      </button>
                      <button
                        type="button"
                        onClick={handleBackToRegister}
                        className={styles.linkButton}
                        disabled={loading}
                      >
                        Change Email
                      </button>
                    </div>
                  </>
                )}
              </div>
            </form>

            <div className={styles.dividerRow}>
              <span className={styles.dividerLine} />
              <span className={styles.dividerText}>or</span>
              <span className={styles.dividerLine} />
            </div>

            <p className={styles.footerNote}>
              Already have an account?{" "}
              <Link to="/login" className={styles.footerLink}>
                Sign In
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
